import React, { useState, useEffect } from "react";
import { calculateTaxBenefit, formatCurrency, clamp, parseNumber } from "../utils/calculatorLogic";
import RangeSlider from "./common/RangeSlider";
import { sharePdf } from "../utils/pdf/sharePdf";
import { mapTaxBenefitData } from "../utils/pdf/pdfMapper";
import { createPdfCacheManager } from "../utils/pdf/pdfCacheManager";
import { generatePdf } from "../utils/pdf/generatePdf";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import ReadMoreText from "./ReadMoreText";
ChartJS.register(ArcElement, Tooltip, Legend);

function TaxBenefitCalculator({ showHeader, headerTitle, headerDesc }) {
  const [grossIncome, setGrossIncome] = useState(1800000);
  const [interestPaid, setInterestPaid] = useState(50000);
  const [principalPaid, setPrincipalPaid] = useState(36000);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isSharingPdf, setIsSharingPdf] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const APPLY_URL = "/property-loan/apply";
  const DEFAULT_HEADER_TITLE = "Tax Benefit & Planning";
  const DEFAULT_HEADER_DESC = "Estimate how much loan you can get based on your income and financial details. Plan your home purchase with confidence.";
  const shouldShowHeader = showHeader?.toLowerCase() === "true";
  const resolvedHeaderTitle = headerTitle?.trim() || DEFAULT_HEADER_TITLE;
  const resolvedHeaderDesc = headerDesc?.trim() || DEFAULT_HEADER_DESC;

  const { taxBeforeLoan, taxAfterLoan, taxBenefit } = calculateTaxBenefit({grossIncome,interestPaid,principalPaid,});
  const chartData = {
    labels: ["Tax Saved", "Remaining Tax"],
    datasets: [
      {
        data: [taxBenefit, taxAfterLoan],
         backgroundColor: ["#5563c1", "#e04644"],
        borderWidth: 0,
        cutout: "70%",
        hoverBorderWidth: 2,
        hoverBorderColor: ["#5168C1", "#E04848"],
        hoverOffset: 0,
        borderRadius: 0,
        spacing: 0
      },
    ],
  };



  const chartOptions = {
    rotation: -90,
    circumference: 180,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
  enabled: false, // ❗ disable default
  external: function (context) {
    let tooltipEl = document.getElementById("glass-tooltip");

    // create element
    if (!tooltipEl) {
      tooltipEl = document.createElement("div");
      tooltipEl.id = "glass-tooltip";
      tooltipEl.innerHTML = `<div class="glass-tooltip-inner"></div>`;
      document.body.appendChild(tooltipEl);
    }

    const tooltip = context.tooltip;

    // hide
    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = 0;
      return;
    }

    const data = tooltip.dataPoints?.[0];
    if (!data) return;

    // set content
    tooltipEl.querySelector(".glass-tooltip-inner").innerHTML = `
      <div class="tooltip-title">${data.label}</div>
      <div class="tooltip-value">₹ ${data.raw.toLocaleString()}</div>
    `;

    // position
    const rect = context.chart.canvas.getBoundingClientRect();

tooltipEl.style.opacity = 1;
tooltipEl.style.position = "absolute";
tooltipEl.style.left =
  rect.left + window.scrollX + tooltip.caretX + "px";
tooltipEl.style.top =
  rect.top + window.scrollY + tooltip.caretY + "px";
tooltipEl.style.transform = "translate(-50%, -100%)";
tooltipEl.style.pointerEvents = "none";
  }
},
    },
  };
  const pdfManager = React.useRef(createPdfCacheManager({pdfType: "taxBenefit",generatePdf,debounceMs: 400,})).current;

  const updateGrossIncome = (val) => { pdfManager.markInteracted(); setGrossIncome(val); };
  const updateInterestPaid = (val) => { pdfManager.markInteracted(); setInterestPaid(val); };
  const updatePrincipalPaid = (val) => { pdfManager.markInteracted(); setPrincipalPaid(val); };

  useEffect(() => {
    const mapped = mapTaxBenefitData({grossIncome,interestPaid,principalPaid,taxBeforeLoan,taxAfterLoan,taxBenefit,});
    setPdfData(mapped);
    pdfManager.scheduleCache(mapped);
  }, [grossIncome,interestPaid,principalPaid,taxBeforeLoan,taxAfterLoan,taxBenefit,]);
const shadowPlugin = {
  id: "shadowPlugin",
  beforeDraw(chart) {
    const { ctx } = chart;

    ctx.save();

    ctx.shadowColor = "rgba(0,0,0,0.32)"; // #00000052
    ctx.shadowBlur = 7.5;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 5;
  },
  afterDraw(chart) {
    chart.ctx.restore();
  }
};
const hoverGlowPlugin = {
  id: "hoverGlowPlugin",
  afterDatasetDraw(chart, args) {
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);

    meta.data.forEach((arc, index) => {
      if (arc.active && index === 0) { // only blue part
        ctx.save();

        ctx.shadowColor = "#5168C1";
        ctx.shadowBlur = 15;

        arc.draw(ctx);

        ctx.restore();
      }
    });
  }
};
  const handlePdfDownload = async () => {
    if (!pdfData) return;
    setIsDownloadingPdf(true);
    try {
      const blob = await pdfManager.getBlob(pdfData);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Tax_Benefit_Report.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const handlePdfShare = async () => {
    if (!pdfData) return;
    setIsSharingPdf(true);
    try {
      const blob = await pdfManager.getBlob(pdfData);
      await sharePdf(blob, "Tax_Benefit_Report.pdf");
    } finally {
      setIsSharingPdf(false);
    }
  };

  return (
  <div className="calc mt-4">
  {shouldShowHeader && (
            <div className="paradescwrap mobile-only-header">
              <p className="title large-body bold">{resolvedHeaderTitle}</p>
              <div className="description">
                <p className="description-text regular-body regular neutral800">
                  <ReadMoreText text={resolvedHeaderDesc} limit={200} />
                </p>
              </div>
            </div>
          )}
    <div class="wrapthechart">
        <div class="chartwrapperformobile">
       <div className="chart-wrapper-mobile">
         <Doughnut data={chartData} options={chartOptions} plugins={[shadowPlugin, hoverGlowPlugin]}/>
         <div className="chart-center">
           <p class="your-emi">Tax Benefit</p>
           <p className="currency">
             ₹ {formatCurrency(Math.round(taxBenefit))}
           </p>
           <span>Annual Savings</span>
         </div>
       </div>
       <div class="donughtmobile">
         <div className="legend-mobile d-flex">
           <div className="legend-item"><span className="dot principal-dot"></span><span className="principal font-w-700 font-14 neutral800">Tax Saved</span></div>
           <div className="legend-item"><span className="dot interest-dot"></span><span className="font-w-700 font-14 neutral800 interest">Tax After Loan</span></div>
         </div>
       </div>
      </div>
    <div className="emi-container max-w-desktop">
      <div className="emi-layout">
        <div className="emi-left">
          {shouldShowHeader && (
            <div className="paradescwrap">
              <p className="title large-body bold">{resolvedHeaderTitle}</p>
              <div className="description">
                <p className="description-text regular-body regular neutral800">
                  <ReadMoreText text={resolvedHeaderDesc} limit={200} />
                </p>
              </div>
            </div>
          )}

          <div className="input-group">
            <div className="input-header">
              <label>Yearly Gross Income</label>
              <div className="input-box">
                <span>₹</span>
                <input type="text" value={formatCurrency(grossIncome)} min="200000" max="5000000" step="50000" onChange={(e) =>updateGrossIncome(clamp(parseNumber(e.target.value), 0, 5000000))}/>
              </div>
            </div>
            <RangeSlider
              min={200000} max={5000000} step={50000} value={grossIncome}
              onChange={(e) => updateGrossIncome(clamp(Number(e.target.value), 200000, 5000000)) }
              formatValue={(val) => val >= 100000 ? `₹ ${(val / 100000).toFixed(0)} L` : `₹ ${formatCurrency(val)}`}
              marks={[
                { value: 200000, label: "2L" },
                { value: 1000000, label: "10L" },
                { value: 1800000, label: "18L" },
                { value: 3600000, label: "36L" },
                { value: 4400000, label: "44L" },
                { value: 5000000, label: "50L" }
              ]}
            />
          </div>

          <div className="input-group">
            <div className="input-header">
              <label>Yearly Interest Paid</label>
              <div className="input-box">
                <span>₹</span>
                <input type="text" value={formatCurrency(interestPaid)} min="5000" max="125000" step="5000" onChange={(e) => updateInterestPaid(clamp(parseNumber(e.target.value), 0, 125000))}/>
              </div>
            </div>
            <RangeSlider
              min={5000} max={125000} step={5000} value={interestPaid} onChange={(e) => updateInterestPaid(clamp(Number(e.target.value), 5000, 125000))}
              formatValue={(val) => val >= 100000 ? `₹ ${(val / 100000).toFixed(2)} L` : `₹ ${(val / 1000).toFixed(0)} K`}
              marks={[
                { value: 5000, label: "5K" },
                { value: 25000, label: "25K" },
                { value: 50000, label: "50K" },
                { value: 75000, label: "75K" },
                { value: 100000, label: "1L" },
                { value: 125000, label: "1.25L" }
              ]}
            />
            </div>

          <div className="input-group">
            <div className="input-header">
              <label>Yearly Principal Paid</label>
              <div className="input-box">
                <span>₹</span>
                <input type="text" value={formatCurrency(principalPaid)} min="2000" max="50000" step="2000" onChange={(e) =>updatePrincipalPaid(clamp(parseNumber(e.target.value), 0, 50000))}/>
              </div>
            </div>
            <RangeSlider
              min={2000} max={50000} step={2000} value={principalPaid}
              onChange={(e) => updatePrincipalPaid(clamp(Number(e.target.value), 2000, 50000))}
              formatValue={(val) => `₹ ${(val / 1000).toFixed(0)} K`}
              marks={[
                { value: 2000, label: "2K" },
                { value: 10000, label: "10K" },
                { value: 20000, label: "20K" },
                { value: 30000, label: "30K" },
                { value: 40000, label: "40K" },
                { value: 50000, label: "50K" }
              ]}
            />
          </div>
        </div>
        <div className="emi-right">
           <div class="emibg">
          <div className="emi-curvature">
            <div className="chart-wrapper">
              <Doughnut data={chartData} options={chartOptions} />
              <div className="chart-center">
                <p class="your-emi">Tax Benefit</p>
                <p className="currency">
                  ₹ {formatCurrency(Math.round(taxBenefit))}
                </p>
                <span>Annual Savings</span>
              </div>
            </div>

            <div className="legend">
              <div className="legend-item"><span className="dot principal-dot"></span><span className="principal font-w-700 font-14 neutral800">Tax Saved</span></div>
              <div className="legend-item"><span className="dot interest-dot"></span><span className="font-w-700 font-14 neutral800 interest">Tax After Loan</span></div>
            </div>

            <div className="summary">
              <div className="summary-row">
                <span className="font-w-500 font-14 neutral600 prin">Tax Before Loan</span>
               <span className="rupee font-w-700 font-14 neutral1000">₹ <span className="numericvalue rupee font-w-500 font-14 neutral1000">{formatCurrency(Math.round(taxBeforeLoan))}</span></span>
              </div>
              <div className="summary-row">
                <span className="font-w-500 font-14 neutral600 interesttotal">Tax After Loan</span>
                  <span className="rupee font-w-700 font-14 neutral1000">₹ <span className="numericvaluerupee font-w-500 font-14 neutral1000">{formatCurrency(Math.round(taxAfterLoan))}</span></span>
              </div>
              <div className="summary-row">
                <span className="font-w-500 font-14 neutral600">Total Tax Saved</span>
                  <span className="rupee font-w-700 font-14 neutral1000"> ₹<span className="numericvalue rupee font-w-500 font-14 neutral1000"> {formatCurrency(Math.round(taxBenefit))} </span></span>
              </div>
            </div>

            <div className="card-actions">
              <div className="icon-group">
                <button className="icon-btn" onClick={handlePdfShare} disabled={isSharingPdf}>
                    <img src={`/documents/d/global/share-icon`} alt="Share"/>
                    <span className="caption-body neutral800">
                      {isSharingPdf ? "Please wait.." : "Share"}
                    </span>
                </button>
                <button className="icon-btn" onClick={handlePdfDownload} disabled={isDownloadingPdf}>
                    <img src={`/documents/d/global/download-icon`} alt="Download"/>
                    <span className="caption-body neutral800">
                      {isDownloadingPdf ? "Please wait.." : "Download"}
                    </span>
                </button>
              </div>
            </div>
          </div>
          </div>

           <div className="left-actions other-calculator">
             <button className="apply-btn btn primary-button" onClick={() => { globalThis.location.href = APPLY_URL; }}>Apply Now</button>
             <button className="callback-btn shimmer-button" onClick={() => globalThis.openApplyModal?.()}>
               <span className="shimmer-text">Get a call back</span>
               <svg className="shimmer-border" viewBox="0 0 232 48" preserveAspectRatio="none">
                 <rect x="1" y="1" width="230" height="46" rx="24" ry="24" pathLength="1"></rect>
               </svg>
             </button>
           </div>
        </div>
      </div>
    </div>
    </div>
	</div>
  );
}

export default TaxBenefitCalculator;